import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import React from 'react';
import {Box, styled} from '@mui/material';
import {Body, designSystemToken} from '@lightricks/react-design-system';
import useUiInteractionEvent from '@contentlab/hooks/analytics/use-interaction-event';
import videoPlaybackStore, {
  VideoPlaybackState,
} from '@contentlab/stores/videoPlaybackStore';
import {SrtItem} from '@contentlab/utils/srtUtils';
import {
  CONTENT_LAB_VIEW_NAMES,
  FLOW_NAMES,
} from '@/lib/analytics/analyticsConstants';
import LEGACY_COLORS from '@/config/legacyColors';
import {DURATION_MINUTES_SECONDS_FORMAT} from '@/config/timeFormats';
import SrtRowEditor from '../srt-row-editor/SrtRowEditor';
import {formatTime} from '../util';

dayjs.extend(duration);

const SrtRowDisplay = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isHighlighted',
})<{isHighlighted?: boolean}>(({isHighlighted}) => ({
  'cursor': 'pointer',
  'padding': '8px',
  'borderRadius': '8px',
  'backgroundColor': isHighlighted
    ? designSystemToken('semantic.bg.tertiary')
    : designSystemToken('semantic.bg.primary'),
  'transition': 'background-color 0.2s ease-in-out',
  '&:hover': {
    backgroundColor: designSystemToken('semantic.bg.tertiary'),
  },
}));

function SrtRow({
  item,
  isEditing,
  isEdited,
  currentIndex,
  setCurrentIndex,
  editRow,
  restoreOriginal,
}: {
  item: SrtItem;
  isEditing: boolean;
  isEdited: boolean;
  currentIndex: string | null;
  setCurrentIndex: (index: string | null) => void;
  editRow: (newText: string) => void;
  restoreOriginal: () => void;
}) {
  const {uiInteraction} = useUiInteractionEvent();
  const videoTime = videoPlaybackStore(
    (state: VideoPlaybackState) => state.currentTime
  );

  const isHighlighted =
    videoTime >= item.startSeconds && videoTime <= item.endSeconds;

  return (
    <Box>
      {isEditing ? (
        <SrtRowEditor
          item={item}
          editRow={(newText: string, shouldCloseRow: boolean) => {
            editRow(newText);
            if (shouldCloseRow) {
              setCurrentIndex(null);
            }
          }}
          isEdited={isEdited}
          restoreOriginal={restoreOriginal}
          currentIndex={currentIndex}
          setCurrentIndex={setCurrentIndex}
        />
      ) : (
        <SrtRowDisplay
          isHighlighted={isHighlighted}
          onClick={() => {
            setCurrentIndex(item.id);
            uiInteraction({
              item_name: 'captions_row_pressed',
              interaction_location: CONTENT_LAB_VIEW_NAMES.CAPTIONS_ROW_EDITOR,
              asset_id: String(item.id),
              flow_name: FLOW_NAMES.CONTENT_LAB.CAPTIONS,
            });
          }}
        >
          <Body
            size="lg"
            color={designSystemToken('semantic.fg.inverse-secondary')}
          >
            {`${formatTime(
              item.startTime,
              DURATION_MINUTES_SECONDS_FORMAT
            )} → ${formatTime(item.endTime, DURATION_MINUTES_SECONDS_FORMAT)}`}
          </Body>
          <Body
            size="xl"
            color={
              isEdited
                ? LEGACY_COLORS.CONTENT_STUDIO_BRAND
                : designSystemToken('semantic.fg.primary')
            }
          >
            {item.currentText}
          </Body>
        </SrtRowDisplay>
      )}
    </Box>
  );
}

export default SrtRow;
